import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useMemo } from "react";
import { WebRuleType } from "types/web-rule-type";
import { WebRule } from "types/web-rule";
import { categorizeWebRules, mergeWebRulesByTitle } from "utils/web-rule-utils";
import { getBookableTimeSets, getWaitListTimeSets } from "utils/time-slot-filters";
import { TimeSlot } from "types/time-slot";

type OrderedWebRule = { type: WebRuleType; webRule: WebRule };

interface CategorizedWebRulesResult {
    /**
     * Ordered sequence preserving original order with filtered bookable times
     * Comment-only sections are preserved as-is
     */
    orderedSequence: OrderedWebRule[];

    /**
     * Activity modal sequence preserving original order with bookable and waitlist times merged
     * into the same visible section.
     */
    activityOrderedSequence: OrderedWebRule[];

    /**
     * Interactive WebRules (have time slots)
     * UNFILTERED - contains all times before bookable/waitlist split
     */
    interactive: WebRule[];

    /**
     * Comment-only WebRules (no time slots or isTextRow)
     */
    commentOnly: WebRule[];

    /**
     * Bookable WebRules - only times with available seats for guest count
     * Merged by title
     */
    bookable: WebRule[];

    /**
     * Waitlist WebRules - only times with waitlist seats for guest count
     * Merged by title
     */
    waitList: WebRule[];
}

const getTimeKey = (time: TimeSlot) => time.start.format("YYYY-MM-DD HH:mm:ss");

const sortByStartTime = (a: TimeSlot, b: TimeSlot) => a.start.valueOf() - b.start.valueOf();

function mergeActivityTimes(bookableRule?: WebRule, waitListRule?: WebRule) {
    const timesByStart = new Map<string, TimeSlot>();

    waitListRule?.times.forEach((time) => {
        timesByStart.set(getTimeKey(time), time);
    });

    bookableRule?.times.forEach((time) => {
        timesByStart.set(getTimeKey(time), time);
    });

    return Array.from(timesByStart.values()).sort(sortByStartTime);
}

function getActivityOrderedSequence(
    ordered: OrderedWebRule[],
    mergedBookable: WebRule[],
    mergedWaitList: WebRule[]
): OrderedWebRule[] {
    const bookableByTitle = new Map(mergedBookable.map((webRule) => [webRule.title, webRule]));
    const waitListByTitle = new Map(mergedWaitList.map((webRule) => [webRule.title, webRule]));
    const renderedInteractiveTitles = new Set<string>();

    return ordered
        .map((item) => {
            if (item.type === WebRuleType.CommentOnly) {
                return item;
            }

            const { title } = item.webRule;
            if (renderedInteractiveTitles.has(title)) {
                return null;
            }

            renderedInteractiveTitles.add(title);

            const bookableRule = bookableByTitle.get(title);
            const waitListRule = waitListByTitle.get(title);
            const mergedTimes = mergeActivityTimes(bookableRule, waitListRule);

            if (mergedTimes.length === 0) {
                return null;
            }

            return {
                ...item,
                webRule: new WebRule({
                    ...(bookableRule ?? waitListRule ?? item.webRule),
                    times: mergedTimes,
                }),
            };
        })
        .filter((item): item is OrderedWebRule => item !== null);
}

/**
 * Hook that encapsulates the complete flow of categorizing and filtering WebRules
 *
 * Flow:
 * 1 Categorize into interactive (has times) vs comment-only (no times)
 * 2 Filter interactive rules into bookable vs waitlist based on guest count
 * 3 Reconstruct ordered sequences for bookable-only and activity/standard merged rendering
 *
 * This ensures that:
 * - The ordered sequence only contains bookable times (no duplicates with waitlist)
 * - The activity ordered sequence merges bookable and waitlist times into one visible section
 * - Comment-only sections are preserved
 *
 * @param webRules - Raw WebRules from API
 * @returns Categorized and filtered WebRules ready for rendering
 */
function useCategorizedWebRules(webRules: WebRule[]): CategorizedWebRulesResult {
    const { guests: stateGuests, isBookingRequest } = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const guests = stateGuests ?? booking?.guests ?? 0;

    return useMemo(() => {
        const { ordered, interactive, commentOnly } = categorizeWebRules(webRules);

        // Filter interactive rules into bookable and waitlist
        // In booking request mode, all times are bookable
        let bookableWebRules: WebRule[];
        let waitListWebRules: WebRule[];

        if (isBookingRequest) {
            bookableWebRules = interactive;
            waitListWebRules = [];
        } else {
            bookableWebRules = getBookableTimeSets(interactive, guests);
            waitListWebRules = getWaitListTimeSets(interactive, guests);
        }

        // Merge bookable and waitlist separately by title
        const mergedBookable = mergeWebRulesByTitle(bookableWebRules);
        const mergedWaitList = mergeWebRulesByTitle(waitListWebRules);

        // Reconstruct ordered sequence with filtered bookable WebRules
        // - Comment-only sections: keep as-is
        // - Interactive sections: replace with filtered bookable version
        // - Remove interactive sections that have no bookable times after filtering
        const orderedWithFiltered = ordered
            .map((item) => {
                if (item.type === WebRuleType.Interactive) {
                    const filteredRule = mergedBookable.find((r) => r.id === item.webRule.id);

                    // Only include if it passed the filters and has times
                    if (filteredRule && filteredRule.times.length > 0) {
                        return { ...item, webRule: filteredRule };
                    }

                    // Filtered out - no bookable times available
                    return null;
                }

                // Keep all comment-only sections unchanged
                return item;
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);

        const activityOrderedSequence = getActivityOrderedSequence(
            ordered,
            mergedBookable,
            mergedWaitList
        );

        return {
            orderedSequence: orderedWithFiltered,
            activityOrderedSequence,
            interactive,
            commentOnly,
            bookable: mergedBookable,
            waitList: mergedWaitList,
        };
    }, [webRules, guests, isBookingRequest]);
}

export { useCategorizedWebRules };
